import { sha256 } from '../worker/receipt-validation';
interface Result<T = unknown> { success: boolean; data?: T; error?: string; message?: string }
interface ReceiptApi {
    createExpense(data: Record<string, unknown>, key?: string): Promise<Result>;
    mergeDuplicate(id: string, data: Record<string, unknown>): Promise<Result>;
    uploadDocument(id: string, file: File, type: string, primary?: boolean): Promise<Result>;
    beginOperation(data:Record<string,unknown>):Promise<Result>;
    completeOperation(key:string):Promise<Result>;
}
interface Recovery {key:string;prepared:boolean;fileHash?:string;fileSize?:number}
// The server retains the frozen draft and operation key. Browser recovery stores
// neither original bytes nor medical metadata in localStorage/IndexedDB.
export class ReceiptSaveSession {
    readonly key:string;
    expenseId: string | null = null;
    private inFlight: Promise<string> | null = null;
    private complete = false;
    private prepared:boolean;
    private readonly draft: Record<string, unknown>;
    constructor(private api: ReceiptApi, draft: Record<string, unknown>, private file: File | null,
        private documentType: string, private targetId?: string,private recovery?:Recovery) {
        this.draft=structuredClone(draft);this.key=recovery?.key || crypto.randomUUID();this.prepared=recovery?.prepared || false;
    }
    save(): Promise<string> {
        if (this.inFlight) return this.inFlight;
        this.inFlight = this.perform().finally(() => { this.inFlight = null; });
        return this.inFlight;
    }
    private async perform(): Promise<string> {
        if (this.complete) return this.expenseId!;
        if (this.recovery?.fileHash && !this.file) {
            const confirmation=await this.api.completeOperation(this.key);
            if (confirmation.success && confirmation.data) {
                this.expenseId=(confirmation.data as {expense_id:string}).expense_id;this.complete=true;return this.expenseId;
            }
            throw new Error('Reselect the matching original file to finish recovery. No OCR is needed.');
        }
        const fileHash=this.file ? await sha256(await this.file.arrayBuffer()):null;
        if (this.recovery?.fileHash && (fileHash!==this.recovery.fileHash || this.file?.size!==this.recovery.fileSize)) {
            throw new Error('This is not the original file for the pending save.');
        }
        if (!this.prepared) {
            const prepared=await this.api.beginOperation({key:this.key,kind:'receipt',expense_id:this.targetId || null,draft:this.draft,
                file:this.file ? {sha256:fileHash,size:this.file.size,document_type:this.documentType}:null});
            if (!prepared.success) throw new Error(prepared.error || 'Unable to preserve the save for recovery. Retry this save.');
            this.prepared=true;
        }
        if (!this.expenseId) {
            const result = this.targetId
                ? await this.api.mergeDuplicate(this.targetId, this.draft)
                : await this.api.createExpense(this.draft, this.key);
            if (!result.success || !result.data || typeof (result.data as { id?: unknown }).id !== 'string') {
                throw new Error(result.error || result.message || 'Expense was not confirmed saved. Retry this save.');
            }
            this.expenseId = (result.data as { id: string }).id;
        }
        if (this.file) {
            const result = await this.api.uploadDocument(this.expenseId, this.file, this.documentType, !this.targetId);
            if (!result.success || !result.data) throw new Error(result.error || result.message || 'Original document was not confirmed stored. Retry this save.');
        }
        const confirmation=await this.api.completeOperation(this.key);
        if (!confirmation.success) throw new Error(confirmation.error || 'Save confirmation interrupted. Retry with the same key.');
        this.complete = true;
        return this.expenseId;
    }
}
